import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Printer } from "lucide-react";
import { api, errMsg, formatR, ORDER_STATUS_LABELS } from "@/lib/api";

const STATUSES = Object.keys(ORDER_STATUS_LABELS);

async function downloadFile(url, params, filename) {
  const r = await api.get(url, { params, responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [outstanding, setOutstanding] = useState({}); // {orderId: {productId: qty}}
  const [savingOut, setSavingOut] = useState("");
  const [codeInput, setCodeInput] = useState({}); // {orderId: "123456"}
  const [confirming, setConfirming] = useState("");
  const [exporting, setExporting] = useState("");
  const [myRole, setMyRole] = useState("admin");

  // Staff accounts may not cancel, refund or manually complete orders (admin-only)
  const allowedStatuses = (current) =>
    STATUSES.filter((s) => myRole !== "staff" || !["cancelled", "refunded", "completed"].includes(s) || s === current);

  const load = (status = filter) =>
    api.get("/admin/orders", { params: status ? { status } : {} }).then((r) => setOrders(r.data.orders)).catch(() => {});

  useEffect(() => {
    load("");
    api.get("/auth/me").then((r) => setMyRole(r.data.role || "admin")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportOrders = async (format) => {
    setExporting(format);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = filter ? `-${filter}` : "";
      await downloadFile("/admin/orders/export",
        { format, ...(filter ? { status: filter } : {}) },
        `btmedu-orders${suffix}-${stamp}.${format}`);
      toast.success(`Orders exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting("");
    }
  };

  const downloadSlip = async (o) => {
    try {
      await downloadFile(`/admin/orders/${o.id}/picklist.pdf`, {}, `picking-slip-${o.order_number}.pdf`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(`${order.order_number} → ${ORDER_STATUS_LABELS[status]}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const confirmHandover = async (o) => {
    const code = (codeInput[o.id] || "").trim();
    if (code.length !== 6) {
      toast.error("Enter the customer's 6-digit code");
      return;
    }
    setConfirming(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/confirm-delivery`, { code });
      toast.success(`${o.order_number} confirmed — customer received their order`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setConfirming("");
    }
  };

  const outVal = (o, item) =>
    outstanding[o.id]?.[item.product_id] ?? item.outstanding_qty ?? 0;

  const setOutVal = (o, item, v) =>
    setOutstanding((s) => ({
      ...s,
      [o.id]: { ...(s[o.id] || {}), [item.product_id]: Math.max(0, Math.min(item.qty, v)) },
    }));

  const saveOutstanding = async (o) => {
    setSavingOut(o.id);
    try {
      const items = o.items.map((i) => ({ product_id: i.product_id, outstanding: outVal(o, i) }));
      await api.patch(`/admin/orders/${o.id}/outstanding`, { items });
      const total = items.reduce((s, i) => s + i.outstanding, 0);
      toast.success(total > 0
        ? `${o.order_number}: ${total} item(s) marked outstanding — customer emailed, follow-up delivery is FREE`
        : `${o.order_number}: all items fulfilled`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingOut("");
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setFilter(""); load(""); }} className={`text-xs font-bold px-4 py-2 rounded-full ${!filter ? "bg-navy text-white" : "bg-white border border-slate-200"}`} data-testid="orders-filter-all">
            All
          </button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => { setFilter(s); load(s); }} className={`text-xs font-bold px-4 py-2 rounded-full ${filter === s ? "bg-navy text-white" : "bg-white border border-slate-200"}`} data-testid={`orders-filter-${s}`}>
              {ORDER_STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportOrders("xlsx")} disabled={!!exporting} className="h-9 px-4 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy flex items-center gap-1.5 hover:border-gold disabled:opacity-40 transition-colors" data-testid="orders-export-xlsx">
            <Download size={13} /> {exporting === "xlsx" ? "Exporting…" : "Export Excel"}
          </button>
          <button onClick={() => exportOrders("pdf")} disabled={!!exporting} className="h-9 px-4 rounded-full bg-navy text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 transition-colors" data-testid="orders-export-pdf">
            <FileText size={13} /> {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {orders.length === 0 && (
          <p className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-sm text-slate-500" data-testid="no-orders-admin">
            No orders in this state.
          </p>
        )}
        {orders.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`admin-order-${o.order_number}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="min-w-[120px]">
                <p className="font-mono font-bold text-navy text-sm">{o.order_number}</p>
                <p className="text-[11px] text-slate-400">{new Date(o.created_at).toLocaleString("en-ZA")}</p>
              </div>
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-semibold text-navy">{o.customer.name}</p>
                <p className="text-xs text-slate-500">{o.customer.email} · {o.delivery_method}</p>
              </div>
              <span className={`text-[11px] font-bold px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-gold/15 text-gold-hover"}`} data-testid={`payment-status-${o.order_number}`}>
                {o.payment_status}
              </span>
              <span className="font-mono font-bold text-navy">{formatR(o.total)}</span>
              <select
                value={o.status}
                onChange={(e) => setStatus(o, e.target.value)}
                className="h-9 rounded-full border border-slate-200 bg-white pl-3 pr-7 text-xs font-bold text-navy outline-none appearance-none bg-no-repeat cursor-pointer hover:border-gold focus:ring-2 focus:ring-gold transition-colors"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%230B2447' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")", backgroundPosition: "right 10px center" }}
                title={myRole === "staff" ? "Staff accounts cannot cancel, refund or manually complete orders" : undefined}
                data-testid={`status-select-${o.order_number}`}
              >
                {allowedStatuses(o.status).map((s) => (
                  <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>
                ))}
              </select>
              <button onClick={() => setOpen(open === o.id ? null : o.id)} className="text-xs font-bold text-brand" data-testid={`expand-${o.order_number}`}>
                {open === o.id ? "Hide" : "Items"}
              </button>
            </div>
            {open === o.id && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm" data-testid={`items-${o.order_number}`}>
                {o.items.map((i) => (
                  <div key={i.product_id} className="flex justify-between items-center gap-3 flex-wrap">
                    <span className="text-slate-600">
                      {i.qty} × {i.name}{i.variant && <span className="text-brand font-semibold"> — {i.variant}</span>}
                      <span className="block text-[10px] text-slate-400 font-mono">
                        SKU: {i.sku || "—"}{(i.barcode || i.isbn) && <> · Barcode: {i.barcode || "—"} · ISBN: {i.isbn || "—"}</>}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono">{formatR(i.line_total)}</span>
                      <span className="flex items-center gap-1.5" title="Items that will be delivered later — free of charge">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Outstanding</span>
                        <input
                          type="number"
                          min={0}
                          max={i.qty}
                          value={outVal(o, i)}
                          onChange={(e) => setOutVal(o, i, parseInt(e.target.value || "0", 10))}
                          className="w-14 h-8 rounded-lg border border-slate-200 px-2 text-xs font-bold text-center outline-none focus:ring-2 focus:ring-gold"
                          data-testid={`outstanding-input-${o.order_number}-${i.sku || i.product_id.slice(0, 6)}`}
                        />
                      </span>
                      {(i.outstanding_qty ?? 0) > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`outstanding-badge-${o.order_number}-${i.sku || i.product_id.slice(0, 6)}`}>
                          owed: {i.outstanding_qty}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
                <div className="pt-2 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => saveOutstanding(o)}
                    disabled={savingOut === o.id}
                    className="h-9 px-4 rounded-full bg-navy text-white text-xs font-bold disabled:opacity-50 transition-colors"
                    data-testid={`save-outstanding-${o.order_number}`}
                  >
                    {savingOut === o.id ? "Saving…" : "Save outstanding items"}
                  </button>
                  <button
                    onClick={() => downloadSlip(o)}
                    className="h-9 px-4 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy flex items-center gap-1.5 hover:border-gold transition-colors"
                    data-testid={`picklist-${o.order_number}`}
                  >
                    <Printer size={13} /> Picking slip (PDF)
                  </button>
                  <span className="text-[10px] text-slate-400">Customer is emailed automatically · follow-up delivery is FREE</span>
                </div>
                {["dispatched", "ready_for_collection"].includes(o.status) && (
                  <div className="mt-3 rounded-2xl bg-navy/[0.03] border border-navy/10 p-4" data-testid={`handover-${o.order_number}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-navy mb-2">
                      Confirm {o.delivery_method === "delivery" ? "delivery" : "collection"} — customer's 6-digit code
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="e.g. 482913"
                        value={codeInput[o.id] || ""}
                        onChange={(e) => setCodeInput((s) => ({ ...s, [o.id]: e.target.value.replace(/\D/g, "") }))}
                        className="w-36 h-10 rounded-lg border border-slate-200 px-3 text-sm font-mono font-bold tracking-[0.3em] text-center outline-none focus:ring-2 focus:ring-gold"
                        data-testid={`handover-code-${o.order_number}`}
                      />
                      <button
                        onClick={() => confirmHandover(o)}
                        disabled={confirming === o.id}
                        className="h-10 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
                        data-testid={`handover-confirm-${o.order_number}`}
                      >
                        {confirming === o.id ? "Confirming…" : "Confirm received"}
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">
                      The customer's code is on their order page and in their {o.delivery_method === "delivery" ? "dispatch" : "collection"} email.
                      Entering it marks the order completed and stands as proof the customer received the stock.
                    </p>
                  </div>
                )}
                {o.address && (
                  <p className="text-xs text-slate-500 pt-2">
                    Deliver to: {o.address.line1}{o.address.suburb ? `, ${o.address.suburb}` : ""}, {o.address.city}, {o.address.postal_code}
                  </p>
                )}
                {o.notes && <p className="text-xs text-slate-500">Notes: {o.notes}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
